import axios, { type AxiosError, type AxiosInstance, type InternalAxiosRequestConfig } from 'axios';
import type { ApiError, ApiResponse, TokenPair } from '@klaro7/shared-types';

const API_URL = import.meta.env.VITE_API_URL || '/api';

/**
 * Extract a user-friendly error message from an API error
 */
export function extractErrorMessage(error: unknown): string {
  if (axios.isAxiosError(error)) {
    const axiosError = error as AxiosError<ApiError>;

    // API returned a structured error response
    const apiError = axiosError.response?.data?.error;
    if (apiError) {
      // Surface field-level validation messages when present, so the user
      // sees "Partita IVA deve essere 11 cifre" instead of a generic
      // "Validation failed". Backend sends these in `details`.
      if (apiError.details && typeof apiError.details === 'object') {
        const fieldMessages = Object.values(apiError.details).flat().filter(Boolean);
        if (fieldMessages.length > 0) {
          return fieldMessages.join(' · ');
        }
      }
      if (apiError.message) {
        return apiError.message;
      }
    }

    // HTTP status-based messages
    if (axiosError.response?.status) {
      const status = axiosError.response.status;
      if (status === 409) return 'Email già registrata. Prova ad accedere.';
      if (status === 422) return 'Dati non validi. Controlla i campi e riprova.';
      if (status === 429) return 'Troppi tentativi. Riprova tra qualche minuto.';
      if (status === 500) return 'Errore del server. Riprova più tardi.';
      if (status === 502 || status === 503) return 'Servizio temporaneamente non disponibile. Riprova tra qualche minuto.';
    }

    // Network error (no response at all)
    if (axiosError.code === 'ERR_NETWORK' || !axiosError.response) {
      return 'Impossibile contattare il server. Verifica la tua connessione e riprova.';
    }
  }

  if (error instanceof Error) {
    return error.message;
  }

  return 'Si è verificato un errore imprevisto. Riprova.';
}

/**
 * Codice applicativo dell'errore API (es. 'PHONE_ALREADY_USED'), se presente.
 * Legge anche un `code` già attaccato a un Error rilanciato dai service.
 */
export function extractErrorCode(error: unknown): string | undefined {
  if (axios.isAxiosError(error)) {
    const code = (error as AxiosError<ApiError>).response?.data?.error?.code;
    return typeof code === 'string' ? code : undefined;
  }
  if (error instanceof Error) {
    const code = (error as Error & { code?: unknown }).code;
    return typeof code === 'string' ? code : undefined;
  }
  return undefined;
}

// Token storage keys
const ACCESS_TOKEN_KEY = 'klaro7_access_token';
const REFRESH_TOKEN_KEY = 'klaro7_refresh_token';

// Create axios instance
export const api: AxiosInstance = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Token management
export const tokenManager = {
  getAccessToken: (): string | null => localStorage.getItem(ACCESS_TOKEN_KEY),
  getRefreshToken: (): string | null => localStorage.getItem(REFRESH_TOKEN_KEY),

  setTokens: (tokens: TokenPair): void => {
    localStorage.setItem(ACCESS_TOKEN_KEY, tokens.accessToken);
    localStorage.setItem(REFRESH_TOKEN_KEY, tokens.refreshToken);
  },

  clearTokens: (): void => {
    localStorage.removeItem(ACCESS_TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
  },
};

/**
 * Esito di un tentativo di refresh. `fatal` distingue il rifiuto del server
 * (refresh token invalido/ruotato → la sessione è davvero morta) dai
 * fallimenti transitori (rete assente, 5xx durante un deploy, 429): in quel
 * caso i token in localStorage sono ancora validi e NON vanno buttati —
 * cancellarli trasformava ogni blip di rete in un logout forzato.
 */
export interface RefreshOutcome {
  accessToken: string | null;
  fatal: boolean;
}

// Single-flight token refresh shared by the axios interceptor AND the socket
// layer (whose handshake failures never pass through axios). The refresh
// endpoint ROTATES the refresh token, so two parallel refreshes invalidate
// each other — every caller must share one in-flight request. Best-effort:
// non fa mai logout da solo (the axios interceptor remains the authority for
// hard logout/redirect).
let refreshInFlight: Promise<RefreshOutcome> | null = null;

export function refreshTokensDetailed(): Promise<RefreshOutcome> {
  if (!refreshInFlight) {
    refreshInFlight = (async (): Promise<RefreshOutcome> => {
      const refreshToken = tokenManager.getRefreshToken();
      if (!refreshToken) return { accessToken: null, fatal: true };
      try {
        const response = await axios.post<ApiResponse<{ tokens: TokenPair }>>(
          `${API_URL}/auth/refresh`,
          { refreshToken }
        );
        const { tokens } = response.data.data;
        tokenManager.setTokens(tokens);
        return { accessToken: tokens.accessToken, fatal: false };
      } catch (err) {
        // Un altro tab può aver già ruotato il token mentre questa richiesta
        // era in volo: se in localStorage c'è un refresh token diverso da
        // quello appena rifiutato, la sessione è viva — usa i token nuovi.
        const current = tokenManager.getRefreshToken();
        if (current && current !== refreshToken) {
          return { accessToken: tokenManager.getAccessToken(), fatal: false };
        }
        const status = axios.isAxiosError(err) ? err.response?.status : undefined;
        return { accessToken: null, fatal: status === 401 || status === 403 };
      }
    })().finally(() => {
      refreshInFlight = null;
    });
  }
  return refreshInFlight;
}

/** Variante compatibile per il layer socket: solo il token (o null). */
export function refreshTokens(): Promise<string | null> {
  return refreshTokensDetailed().then((r) => r.accessToken);
}

// Request interceptor - add auth header
api.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = tokenManager.getAccessToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor - handle token refresh
api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError<ApiError>) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

    // Handle 401 - attempt token refresh
    if (error.response?.status === 401 && !originalRequest._retry) {
      const refreshToken = tokenManager.getRefreshToken();

      // If user was never logged in (no tokens at all), just reject without redirect
      if (!refreshToken && !tokenManager.getAccessToken()) {
        return Promise.reject(error);
      }

      originalRequest._retry = true;

      if (!refreshToken) {
        tokenManager.clearTokens();
        window.location.href = '/login';
        return Promise.reject(error);
      }

      // Shared single-flight: concurrent 401s (and socket-layer refreshes)
      // all await the same request instead of racing the token rotation.
      const outcome = await refreshTokensDetailed();
      if (outcome.accessToken) {
        originalRequest.headers.Authorization = `Bearer ${outcome.accessToken}`;
        return api(originalRequest);
      }

      if (outcome.fatal) {
        // Il server ha rifiutato il refresh token: sessione davvero finita.
        tokenManager.clearTokens();
        window.location.href = '/login';
      }
      // Fallimento transitorio (rete/5xx/429): i token restano, la richiesta
      // fallisce e verrà ritentata dal chiamante (React Query, prossimo poll).
      return Promise.reject(error);
    }

    // Handle 429 - rate limit
    if (error.response?.status === 429) {
      window.dispatchEvent(new CustomEvent('api:rate-limit'));
    }

    // Forced password change still pending — route the user to the gate.
    if (
      error.response?.status === 403 &&
      error.response.data?.error?.code === 'PASSWORD_CHANGE_REQUIRED' &&
      window.location.pathname !== '/change-password'
    ) {
      window.location.href = '/change-password';
    }

    return Promise.reject(error);
  }
);

export default api;
